import { TaxonomyNode, TaxonomyNodeApi } from "types";

import { decodeNodeIds, encodeNodeValue } from "utils/treeSearch/treeSearchUtils";

/**
 * Represents the value of a taxonomy node in the tree.
 * This is a delimited string which contains the full "path" of the Node.
 *
 * example: "verticals.transportation and travel.3651.3652"
 *
 * The full path is done for 2 major reasons:
 *
 * 1. Ensure the value is unique.
 *    The taxonomy intentionally has duplicate nodes under different parents so the ID alone is not unique in the tree.
 * 2. Support removing an entire group's descendants when its combined pill is removed.
 *    `TreeSearch`'s pill removal matches via `startsWith`, so having the full path in the value
 *    makes this easy and saves us from searching the tree.
 */
export type TaxonomyNodeValue = string;

/**
 * Returns a string that can be used as a `value` for a TaxonomyNode.
 *
 * example: "verticals.transportation and travel.3651.3652"
 */
const encodeTaxonomyNodeValue = (parent: TaxonomyNode | undefined, node: TaxonomyNodeApi): TaxonomyNodeValue =>
  encodeNodeValue(parent?.value, node.taxonomyNodeId ?? node.name.toLocaleLowerCase());

/**
 * Builds a list of distinct Node IDs from an array of TaxonomyNodeValue instances.
 *
 * ["verticals.retail.3483.3486", "verticals.retail.3483.3487"] -> ["3486", "3487"]
 */
const buildTaxonomyNodeApiValues = (selections: TaxonomyNodeValue[]): string[] => decodeNodeIds(selections);

export default {
  encodeValue: encodeTaxonomyNodeValue,
  buildApiValues: buildTaxonomyNodeApiValues,
};
